import { type DeleteStoredViewInput, type DeleteStoredViewMutation } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteStoredViewMutationDocument } from "./stored-view.gql";
import { EVENT_STORED_VIEW_DELETED, trackStoredViewEvent } from "./tracking";

type DeletedStoredView = DeleteStoredViewMutation["deleteStoredView"];

export interface UseDeleteStoredViewProps {
  onCompleted?: (data: DeletedStoredView) => void;
  onError?: (e: Error) => void;
}

export const useDeleteStoredView = ({ onCompleted, onError }: UseDeleteStoredViewProps = {}) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: DeleteStoredViewInput) => {
      const client = createOperationClient("DeleteStoredView");
      const data = await client.request(deleteStoredViewMutationDocument, { input });
      return data.deleteStoredView;
    },
    onSuccess: (deletedStoredView) => {
      trackStoredViewEvent(EVENT_STORED_VIEW_DELETED, deletedStoredView);

      // Invalidate stored view list to refetch with updated data
      queryClient.invalidateQueries({
        queryKey: queryKeys.storedViews.list(deletedStoredView.key, deletedStoredView.workspaceId),
      });

      // Invalidate active view list for the workspace
      queryClient.invalidateQueries({
        queryKey: queryKeys.activeViews.list(deletedStoredView.workspaceId),
      });

      // Call the onCompleted callback
      if (onCompleted) {
        onCompleted(deletedStoredView);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    deleteStoredView: mutation.mutate,
    loading: mutation.isPending,
    error: mutation.error,
  };
};
